/**
 * Phase 13 / Task 13.3 (DEC-351) — the dispatcher Tanks tab (TANK-04).
 *
 * Every active registered tank with its estimated current volume, % full, fill rate,
 * "full by" and last haul, soonest-full first. The headline is the ESTIMATE
 * ("~142 BBL (est.) · full in about 3 days"), the fill rate smaller underneath.
 *
 * When the company has tank estimates switched off (hauler_tenant_settings
 * .tank_estimates_enabled), the page shows recorded data only: the level the last haul
 * left the tank at and when, no estimate, no fill rate, no "full by".
 *
 * Times are the dispatcher's local time (DEC-004 / DEC-066), never UTC.
 *
 * Phase 13.2a — each row has "Enter reading" (TankReadingButton): a hand-entered level the fill
 * rate and the last level use.
 *
 * Tank registry PR 1 (0245) — a "Needs size" marker on a tank whose reading was given in inches
 * while it has no bbl per inch, with a hint to set it under Sites & Operators.
 *
 * OPEN-136 (DEC-377) — a tank with no bbl per inch says so (TankSizeHint): the standard size its
 * estimate uses, with Save for a Dispatch Manager, or the options when the size is ambiguous.
 *
 * 2026-10-21 (Corbin: "this needs to be sortable and filterable"):
 *   - every column header sorts (click again to reverse; an arrow shows which); the default is
 *     still soonest full (Full by, ascending);
 *   - a search box (tank, site/well or customer), Customer and Site/well dropdowns, and status
 *     chips (any of them matches);
 *   - the sort and filters are remembered per user in this browser (tank-levels-format.ts);
 *   - readings that span less than MIN_HISTORY_SPAN_HOURS show "Not enough history" with no
 *     rate, full time or threshold forecast (the shared rule the phone uses, #1013).
 */

import * as React from 'react';
import { useApiOpts } from '@lodex-pro/web-shared';
import { Navigate } from 'react-router-dom';
import { useUserProfile, useUserTimezone } from '../lib/use-user-timezone';
import { useFeatureState } from '../lib/use-features';
import { formatTime, formatTimestamp } from '../lib/timestamp';
import { DashboardTabs } from '../features/dashboard/DashboardTabs';
import { TankReadingButton } from '../features/dashboard/TankReadingButton';
import {
  TANK_LEVELS_QUERY_KEY,
  useTankLevels,
  type TankLevelRow,
} from '../features/dashboard/use-tank-levels';
import { TankSizeHint, suggestionFrom } from '../features/sites-operators/TankSizeHint';
import { MANAGE_CATALOG_ROLES } from '../lib/role-gates';
import {
  DEFAULT_TANK_SORT,
  NO_TANK_FILTERS,
  TANK_STATUSES,
  autoHaulHoldLabel,
  estimateHeadline,
  fillRateLabel,
  filterOptions,
  filterTanks,
  formatEstimateBbl,
  fullInLabel,
  hasTankFilters,
  nextSort,
  noEstimateReason,
  producerRawHeadline,
  pruneTankFilters,
  readTankView,
  shownForecast,
  sortTanks,
  thresholdInLabel,
  writeTankView,
  type TankFilters,
  type TankSortKey,
  type TankSortState,
  type TankStatus,
  type TankView,
} from '../features/dashboard/tank-levels-format';
import { formatNumber } from '@lodex-pro/shared/number-format';

const mutedStyle: React.CSSProperties = { fontSize: '12px', color: 'var(--color-text-tertiary)' };

const cellStyle: React.CSSProperties = {
  padding: '10px 12px',
  borderBottom: '1px solid var(--color-border-primary)',
  verticalAlign: 'top',
  textAlign: 'left',
};

const headStyle: React.CSSProperties = {
  ...cellStyle,
  fontSize: '12px',
  fontWeight: 700,
  color: 'var(--color-text-secondary)',
};

const controlStyle: React.CSSProperties = {
  minHeight: '36px',
  padding: '0 10px',
  borderRadius: '10px',
  border: '1px solid var(--color-border-primary)',
  background: 'var(--color-bg-tertiary)',
  color: 'var(--color-text-primary)',
  fontSize: '14px',
};

function LevelCell({
  row,
  estimatesEnabled,
  timeZone,
}: {
  row: TankLevelRow;
  estimatesEnabled: boolean;
  timeZone: string;
}): React.JSX.Element {
  // 13.8 (D-23c): the producer's latest level has no barrels: show it raw, never an estimate.
  const raw = producerRawHeadline(row);
  if (raw !== null) {
    return (
      <td style={cellStyle} data-testid="tank-level">
        <div style={{ fontSize: '16px', fontWeight: 700 }}>{raw}</div>
        {row.latestReading != null && (
          <div style={mutedStyle}>
            producer&apos;s reading {formatTimestamp(row.latestReading.readAt, timeZone)}
          </div>
        )}
      </td>
    );
  }
  if (!estimatesEnabled) {
    return (
      <td style={cellStyle} data-testid="tank-level">
        {row.lastLevelBbl === null ? (
          <span style={mutedStyle}>No recorded level</span>
        ) : (
          <>
            <div style={{ fontWeight: 600 }}>{formatEstimateBbl(row.lastLevelBbl)} BBL</div>
            <div style={mutedStyle}>
              {row.lastLevelAt === null || row.lastLevelAt === undefined
                ? 'last recorded'
                : `recorded ${formatTimestamp(row.lastLevelAt, timeZone)}`}
            </div>
          </>
        )}
      </td>
    );
  }
  const reason = noEstimateReason(row, estimatesEnabled);
  if (reason !== null || row.estimatedCurrentBbl === null) {
    return (
      <td style={cellStyle} data-testid="tank-level">
        <span style={mutedStyle}>{reason}</span>
      </td>
    );
  }
  const shown = shownForecast(row);
  return (
    <td style={cellStyle} data-testid="tank-level">
      <div style={{ fontSize: '16px', fontWeight: 700 }}>
        {estimateHeadline(row.estimatedCurrentBbl)}
        {shown.predictedFullAt !== null && <> · {fullInLabel(shown.predictedFullAt)}</>}
      </div>
      {shown.shortHistory ? (
        <div style={mutedStyle} data-testid="tank-short-history">
          Not enough history
        </div>
      ) : (
        shown.fillRateBpd !== null && (
          <div style={mutedStyle}>{fillRateLabel(shown.fillRateBpd)}</div>
        )
      )}
    </td>
  );
}

/** A column header that sorts; aria-sort tells a screen reader which way. */
function SortHeader({
  label,
  sortKey,
  sort,
  onSort,
}: {
  label: string;
  sortKey: TankSortKey;
  sort: TankSortState;
  onSort: (key: TankSortKey) => void;
}): React.JSX.Element {
  const active = sort.key === sortKey;
  return (
    <th
      style={headStyle}
      aria-sort={active ? (sort.dir === 'asc' ? 'ascending' : 'descending') : 'none'}
    >
      <button
        type="button"
        onClick={(): void => onSort(sortKey)}
        style={{
          all: 'unset',
          cursor: 'pointer',
          display: 'inline-flex',
          alignItems: 'center',
          gap: '4px',
          color: active ? 'var(--color-text-primary)' : 'inherit',
        }}
      >
        {label}
        <span aria-hidden="true" style={{ opacity: active ? 1 : 0.35 }}>
          {active ? (sort.dir === 'asc' ? '▲' : '▼') : '↕'}
        </span>
      </button>
    </th>
  );
}

/**
 * The signed-in user's sort and filters at this company, remembered in this browser; reloads
 * when the user or company changes. A change made before the profile arrived is kept and stored
 * then, not replaced by the stored view (review L3).
 */
function useTankView(): readonly [TankView, (next: TankView) => void] {
  const profile = useUserProfile();
  const userId = profile.data?.profile.userId ?? null;
  const tenantId = profile.data?.profile.tenantId ?? null;
  const [view, setView] = React.useState<TankView>(() => readTankView(userId, tenantId));
  const viewRef = React.useRef(view);
  viewRef.current = view;
  const changedUnsaved = React.useRef(false);
  React.useEffect(() => {
    if (userId === null || tenantId === null) return;
    if (changedUnsaved.current) {
      changedUnsaved.current = false;
      writeTankView(userId, tenantId, viewRef.current);
      return;
    }
    setView(readTankView(userId, tenantId));
  }, [userId, tenantId]);
  const update = React.useCallback(
    (next: TankView): void => {
      setView(next);
      if (userId === null || tenantId === null) changedUnsaved.current = true;
      else writeTankView(userId, tenantId, next);
    },
    [userId, tenantId],
  );
  return [view, update] as const;
}

function FilterBar({
  filters,
  onChange,
  customers,
  sites,
  shown,
  total,
}: {
  filters: TankFilters;
  onChange: (next: TankFilters) => void;
  customers: readonly string[];
  sites: readonly string[];
  shown: number;
  total: number;
}): React.JSX.Element {
  const toggle = (id: TankStatus): void =>
    onChange({
      ...filters,
      statuses: filters.statuses.includes(id)
        ? filters.statuses.filter((s) => s !== id)
        : [...filters.statuses, id],
    });
  return (
    <div
      style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}
      data-testid="tank-filters"
    >
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'center' }}>
        <input
          type="search"
          aria-label="Search tanks"
          placeholder="Search tank, site or customer"
          value={filters.q}
          onChange={(e): void => onChange({ ...filters, q: e.target.value })}
          style={{ ...controlStyle, minWidth: '220px' }}
        />
        <select
          aria-label="Customer"
          value={filters.customer}
          onChange={(e): void => onChange({ ...filters, customer: e.target.value })}
          style={controlStyle}
        >
          <option value="">All customers</option>
          {customers.map((c) => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
        </select>
        <select
          aria-label="Site or well"
          value={filters.site}
          onChange={(e): void => onChange({ ...filters, site: e.target.value })}
          style={controlStyle}
        >
          <option value="">All sites and wells</option>
          {sites.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
        {hasTankFilters(filters) && (
          <button
            type="button"
            onClick={(): void => onChange(NO_TANK_FILTERS)}
            style={{ ...controlStyle, cursor: 'pointer' }}
          >
            Clear filters
          </button>
        )}
        <span style={mutedStyle} data-testid="tank-count">
          {shown === total
            ? `${formatNumber(total)} tanks`
            : `Showing ${formatNumber(shown)} of ${formatNumber(total)} tanks`}
        </span>
      </div>
      <div
        style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', alignItems: 'center' }}
        role="group"
        aria-label="Status"
      >
        {TANK_STATUSES.map((s) => {
          const on = filters.statuses.includes(s.id);
          return (
            <button
              key={s.id}
              type="button"
              aria-pressed={on}
              onClick={(): void => toggle(s.id)}
              style={{
                ...controlStyle,
                minHeight: '32px',
                borderRadius: 'var(--radius-pill)',
                cursor: 'pointer',
                background: on ? 'var(--color-accent-blue)' : 'var(--color-bg-tertiary)',
                color: on ? 'var(--color-on-accent)' : 'var(--color-text-primary)',
              }}
            >
              {s.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export function TanksDashboardPage(): React.JSX.Element {
  const apiOpts = useApiOpts();
  const timeZone = useUserTimezone();
  const { data, isLoading, isError } = useTankLevels(apiOpts);
  const [view, setView] = useTankView();
  const estimatesEnabled = data?.estimatesEnabled ?? true;
  // DEC-377 — Save on a tank's size is PATCH /v1/tanks/:id (MANAGE_HAULER_CATALOG); a UX gate.
  const { data: profileData } = useUserProfile();
  const canManageTanks = MANAGE_CATALOG_ROLES.has(profileData?.profile.role ?? '');

  const all = React.useMemo(() => data?.tanks ?? [], [data]);
  const customers = React.useMemo(() => filterOptions(all.map((t) => t.customerName)), [all]);
  const sites = React.useMemo(() => filterOptions(all.map((t) => t.siteName)), [all]);
  // Once the tanks are in, a remembered customer or site that none of them has is dropped.
  React.useEffect(() => {
    if (data === undefined) return;
    const pruned = pruneTankFilters(view.filters, customers, sites);
    if (pruned !== view.filters) setView({ ...view, filters: pruned });
  }, [data, customers, sites, view, setView]);
  const rows = React.useMemo(
    () => sortTanks(filterTanks(all, view.filters), view.sort, estimatesEnabled),
    [all, view, estimatesEnabled],
  );
  const onSort = (key: TankSortKey): void => setView({ ...view, sort: nextSort(view.sort, key) });
  const sortProps = { sort: view.sort, onSort };

  return (
    <div
      style={{
        height: '100%',
        overflowY: 'auto',
        padding: '16px',
        display: 'flex',
        flexDirection: 'column',
        gap: '16px',
        backgroundColor: 'var(--color-bg-primary)',
        color: 'var(--color-text-primary)',
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <h2 style={{ margin: 0, fontSize: '18px' }}>Tanks</h2>
          <DashboardTabs />
        </div>
        {data !== undefined && (
          <span style={mutedStyle} data-testid="tanks-updated">
            Updated {formatTime(data.generatedAt, timeZone)}
          </span>
        )}
      </div>

      {all.length > 0 && (
        <FilterBar
          filters={view.filters}
          onChange={(filters): void => setView({ ...view, filters })}
          customers={customers}
          sites={sites}
          shown={rows.length}
          total={all.length}
        />
      )}

      {data !== undefined && !estimatesEnabled && (
        <p style={mutedStyle} data-testid="estimates-off">
          Tank estimates are switched off for your company, so this shows recorded levels only.
        </p>
      )}

      {isLoading && <p style={mutedStyle}>Loading…</p>}
      {isError && (
        <p role="alert" style={{ color: 'var(--color-danger)' }}>
          Tanks could not be loaded. It will retry automatically.
        </p>
      )}

      {data !== undefined && all.length === 0 && (
        <p style={mutedStyle}>
          No registered tanks yet. Add tanks under Sites &amp; Operators to see them here.
        </p>
      )}

      {all.length > 0 && rows.length === 0 && (
        <p style={mutedStyle} data-testid="tanks-none-match">
          No tanks match these filters.{' '}
          <button
            type="button"
            onClick={(): void => setView({ ...view, filters: NO_TANK_FILTERS })}
            style={{ ...controlStyle, minHeight: '28px', cursor: 'pointer' }}
          >
            Clear filters
          </button>
        </p>
      )}

      {rows.length > 0 && (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
            <thead>
              <tr>
                <SortHeader label="Tank" sortKey="TANK" {...sortProps} />
                <SortHeader
                  label={estimatesEnabled ? 'Estimated now' : 'Last level'}
                  sortKey="ESTIMATE"
                  {...sortProps}
                />
                {estimatesEnabled && <SortHeader label="% full" sortKey="PERCENT" {...sortProps} />}
                {estimatesEnabled && (
                  <SortHeader label="Full by" sortKey="FULL_BY" {...sortProps} />
                )}
                <SortHeader label="Last haul" sortKey="LAST_HAUL" {...sortProps} />
                <SortHeader label="Threshold" sortKey="THRESHOLD" {...sortProps} />
                <th style={headStyle}>
                  <span className="sr-only">Readings</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => {
                const shown = shownForecast(row);
                return (
                  <tr key={row.tankId} data-testid="tank-row">
                    <td style={cellStyle}>
                      <div style={{ fontWeight: 600 }}>{row.tankName}</div>
                      <div style={mutedStyle}>{row.siteName ?? '—'}</div>
                      {row.customerName != null && (
                        <div style={mutedStyle} data-testid="tank-customer">
                          {row.customerName}
                        </div>
                      )}
                      {row.needsSize === true && (
                        <div
                          data-testid="tank-needs-size"
                          style={{ ...mutedStyle, color: 'var(--color-warning)' }}
                          title="A reading was entered in inches, but this tank has no bbl per inch. Set it under Sites & Operators."
                        >
                          Needs size · set its bbl per inch in Sites &amp; Operators
                        </div>
                      )}
                      {row.bblPerInch === null && (
                        <TankSizeHint
                          tankId={row.tankId}
                          bblPerInch={row.bblPerInch}
                          suggestion={suggestionFrom(row.bblPerInchChoices)}
                          canManage={canManageTanks}
                          whereToSet="in Sites & Operators"
                          refresh={[TANK_LEVELS_QUERY_KEY]}
                        />
                      )}
                      {row.operatorNeedsSize === true && (
                        <div
                          data-testid="tank-operator-needs-size"
                          style={{ ...mutedStyle, color: 'var(--color-warning)' }}
                          title="The producer read this tank in inches, and their tank has no size yet. Its level is shown as read, with no barrels, until they set it."
                        >
                          Producer&apos;s tank needs a size
                        </div>
                      )}
                    </td>
                    <LevelCell row={row} estimatesEnabled={estimatesEnabled} timeZone={timeZone} />
                    {estimatesEnabled && (
                      <td style={cellStyle}>
                        {row.estimatedPercentFull === null
                          ? '—'
                          : `${Math.round(row.estimatedPercentFull)}%`}
                      </td>
                    )}
                    {estimatesEnabled && (
                      <td style={cellStyle} data-testid="tank-full-by">
                        {shown.predictedFullAt === null
                          ? '—'
                          : formatTimestamp(shown.predictedFullAt, timeZone)}
                      </td>
                    )}
                    <td style={cellStyle}>
                      {row.lastHaulAt === null ? '—' : formatTimestamp(row.lastHaulAt, timeZone)}
                    </td>
                    {/* 13.4a (0236) — the haul threshold and when the tank reaches it. */}
                    <td style={cellStyle} data-testid="tank-threshold">
                      {row.thresholdPercent == null ? (
                        '—'
                      ) : (
                        <>
                          <div style={{ fontWeight: 600 }}>
                            {Math.round(row.thresholdPercent)}%
                            {row.autoHaulOn === true && (
                              <span style={{ ...mutedStyle, marginLeft: '6px' }}>
                                · auto-haul on
                              </span>
                            )}
                          </div>
                          <div style={mutedStyle}>
                            {!estimatesEnabled
                              ? 'forecasts are off'
                              : shown.shortHistory
                                ? 'not enough history'
                                : thresholdInLabel(shown.predictedThresholdAt)}
                          </div>
                          {/* 13.4b (0237) — the sweeper's open job for the tank, or why it held. */}
                          {row.openAutoHaulId != null && (
                            <div style={mutedStyle} data-testid="tank-auto-job">
                              auto job {row.openAutoHaulId}
                            </div>
                          )}
                          {row.autoHaulHoldReason != null && (
                            <div
                              style={{ ...mutedStyle, color: 'var(--color-warning)' }}
                              data-testid="tank-auto-haul-hold"
                            >
                              {autoHaulHoldLabel(row.autoHaulHoldReason)}
                            </div>
                          )}
                        </>
                      )}
                    </td>
                    <td style={cellStyle}>
                      <TankReadingButton tank={row} />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      {view.sort.key !== DEFAULT_TANK_SORT.key || view.sort.dir !== DEFAULT_TANK_SORT.dir ? (
        <button
          type="button"
          onClick={(): void => setView({ ...view, sort: DEFAULT_TANK_SORT })}
          style={{ ...controlStyle, alignSelf: 'flex-start', cursor: 'pointer' }}
        >
          Sort by soonest full again
        </button>
      ) : null}
    </div>
  );
}

/**
 * The /dashboard/tanks route: the Tanks tab is the TANKS_TAB feature (§58 PR 6a, DEC-366).
 * Off → the Operations dashboard, replacing the URL so Back does not bounce through it (the
 * SettingsPage pattern); the page and its /v1/tanks/levels call (which would answer 403
 * FEATURE_NOT_ENABLED) never mount. While the profile loads, or when the features are unknown
 * (the server couldn't read them, none are remembered, or the profile itself failed to load),
 * say so instead of a blank page; useFeatureState keeps asking (#881 and #890 reviews).
 */
export function TanksDashboardRoute(): React.JSX.Element {
  const tanksTab = useFeatureState('TANKS_TAB');
  if (tanksTab === 'OFF') return <Navigate to="/dashboard" replace />;
  if (tanksTab === 'ON') return <TanksDashboardPage />;
  return (
    <div style={{ padding: '24px' }} data-testid="tanks-feature-pending">
      {tanksTab === 'LOADING' ? (
        <p style={mutedStyle}>Loading…</p>
      ) : (
        <p role="status" style={mutedStyle}>
          We couldn&apos;t check your plan just now. Trying again…
        </p>
      )}
    </div>
  );
}
